/**
 * The master list of nutrients beyond calories and the three main macros.
 *
 * To track one more, add a line here (and to the list in scanner/src/extract.ts,
 * which is a separate service and cannot share this file). The database column,
 * the logging form and the Today screen all follow from this list.
 *
 * It lives in server/ because only that folder is copied into the running
 * container, and the front end can import from there but not the other way
 * round. It must stay free of imports, so it can run in both places.
 *
 * `off` is the nutrient's name in Open Food Facts. It is null for fibre, sugar
 * and sodium, which were there before this list and are handled by name.
 */
export const NUTRIENTS = [
  { key: "saturated_fat_g", label: "Saturated fat", unit: "g", group: "Fats", off: "saturated-fat" },
  { key: "trans_fat_g", label: "Trans fat", unit: "g", group: "Fats", off: "trans-fat" },
  { key: "monounsaturated_fat_g", label: "Monounsaturated fat", unit: "g", group: "Fats", off: "monounsaturated-fat" },
  { key: "polyunsaturated_fat_g", label: "Polyunsaturated fat", unit: "g", group: "Fats", off: "polyunsaturated-fat" },
  { key: "omega_3_g", label: "Omega-3", unit: "g", group: "Fats", off: "omega-3-fat" },
  { key: "cholesterol_mg", label: "Cholesterol", unit: "mg", group: "Fats", off: "cholesterol" },

  { key: "fiber_g", label: "Fibre", unit: "g", group: "Carbs", off: null },
  { key: "sugar_g", label: "Sugar", unit: "g", group: "Carbs", off: null },
  { key: "added_sugar_g", label: "Added sugar", unit: "g", group: "Carbs", off: "added-sugars" },
  { key: "sugar_alcohol_g", label: "Sugar alcohols", unit: "g", group: "Carbs", off: "polyols" },
  { key: "starch_g", label: "Starch", unit: "g", group: "Carbs", off: "starch" },

  { key: "sodium_mg", label: "Sodium", unit: "mg", group: "Minerals", off: null },
  { key: "potassium_mg", label: "Potassium", unit: "mg", group: "Minerals", off: "potassium" },
  { key: "calcium_mg", label: "Calcium", unit: "mg", group: "Minerals", off: "calcium" },
  { key: "iron_mg", label: "Iron", unit: "mg", group: "Minerals", off: "iron" },
  { key: "magnesium_mg", label: "Magnesium", unit: "mg", group: "Minerals", off: "magnesium" },
  { key: "zinc_mg", label: "Zinc", unit: "mg", group: "Minerals", off: "zinc" },
  { key: "phosphorus_mg", label: "Phosphorus", unit: "mg", group: "Minerals", off: "phosphorus" },
  { key: "copper_mg", label: "Copper", unit: "mg", group: "Minerals", off: "copper" },
  { key: "manganese_mg", label: "Manganese", unit: "mg", group: "Minerals", off: "manganese" },
  { key: "selenium_mcg", label: "Selenium", unit: "mcg", group: "Minerals", off: "selenium" },
  { key: "iodine_mcg", label: "Iodine", unit: "mcg", group: "Minerals", off: "iodine" },
  { key: "chromium_mcg", label: "Chromium", unit: "mcg", group: "Minerals", off: "chromium" },
  { key: "molybdenum_mcg", label: "Molybdenum", unit: "mcg", group: "Minerals", off: "molybdenum" },

  { key: "vitamin_a_mcg", label: "Vitamin A", unit: "mcg", group: "Vitamins", off: "vitamin-a" },
  { key: "vitamin_c_mg", label: "Vitamin C", unit: "mg", group: "Vitamins", off: "vitamin-c" },
  { key: "vitamin_d_mcg", label: "Vitamin D", unit: "mcg", group: "Vitamins", off: "vitamin-d" },
  { key: "vitamin_e_mg", label: "Vitamin E", unit: "mg", group: "Vitamins", off: "vitamin-e" },
  { key: "vitamin_k_mcg", label: "Vitamin K", unit: "mcg", group: "Vitamins", off: "vitamin-k" },
  { key: "thiamin_mg", label: "Thiamin (B1)", unit: "mg", group: "Vitamins", off: "vitamin-b1" },
  { key: "riboflavin_mg", label: "Riboflavin (B2)", unit: "mg", group: "Vitamins", off: "vitamin-b2" },
  { key: "niacin_mg", label: "Niacin (B3)", unit: "mg", group: "Vitamins", off: "vitamin-pp" },
  { key: "pantothenic_acid_mg", label: "Pantothenic acid (B5)", unit: "mg", group: "Vitamins", off: "pantothenic-acid" },
  { key: "vitamin_b6_mg", label: "Vitamin B6", unit: "mg", group: "Vitamins", off: "vitamin-b6" },
  { key: "biotin_mcg", label: "Biotin (B7)", unit: "mcg", group: "Vitamins", off: "biotin" },
  { key: "folate_mcg", label: "Folate (B9)", unit: "mcg", group: "Vitamins", off: "vitamin-b9" },
  { key: "vitamin_b12_mcg", label: "Vitamin B12", unit: "mcg", group: "Vitamins", off: "vitamin-b12" },
  { key: "choline_mg", label: "Choline", unit: "mg", group: "Vitamins", off: "choline" },
] as const;

/**
 * How much of each nutrient an adult should have in a day: the US FDA's Daily
 * Values, the same numbers behind the "% Daily Value" on food labels.
 *
 * `limit: true` means it is a most-you-should-have rather than an amount to
 * reach. Anything missing here has no official daily amount, so the Today
 * screen shows how much was logged and no percentage.
 */
export const DAILY_TARGETS: Partial<Record<NutrientKey, { amount: number; limit?: true }>> = {
  saturated_fat_g: { amount: 20, limit: true },
  cholesterol_mg: { amount: 300, limit: true },

  fiber_g: { amount: 28 },
  added_sugar_g: { amount: 50, limit: true },

  sodium_mg: { amount: 2300, limit: true },
  potassium_mg: { amount: 4700 },
  calcium_mg: { amount: 1300 },
  iron_mg: { amount: 18 },
  magnesium_mg: { amount: 420 },
  zinc_mg: { amount: 11 },
  phosphorus_mg: { amount: 1250 },
  copper_mg: { amount: 0.9 },
  manganese_mg: { amount: 2.3 },
  selenium_mcg: { amount: 55 },
  iodine_mcg: { amount: 150 },
  chromium_mcg: { amount: 35 },
  molybdenum_mcg: { amount: 45 },

  vitamin_a_mcg: { amount: 900 },
  vitamin_c_mg: { amount: 90 },
  vitamin_d_mcg: { amount: 20 },
  vitamin_e_mg: { amount: 15 },
  vitamin_k_mcg: { amount: 120 },
  thiamin_mg: { amount: 1.2 },
  riboflavin_mg: { amount: 1.3 },
  niacin_mg: { amount: 16 },
  pantothenic_acid_mg: { amount: 5 },
  vitamin_b6_mg: { amount: 1.7 },
  biotin_mcg: { amount: 30 },
  folate_mcg: { amount: 400 },
  vitamin_b12_mcg: { amount: 2.4 },
  choline_mg: { amount: 550 },
};

export const NUTRIENT_GROUPS = ["Fats", "Carbs", "Minerals", "Vitamins"] as const;

type Nutrient = (typeof NUTRIENTS)[number];

/** Any nutrient in the list. */
export type NutrientKey = Nutrient["key"];

/** The nutrients that are new columns, rather than the original fibre, sugar and sodium. */
export type ExtraNutrient = Extract<Nutrient, { off: string }>;
export type ExtraKey = ExtraNutrient["key"];

export const EXTRA_NUTRIENTS = NUTRIENTS.filter((n): n is ExtraNutrient => n.off !== null);
